<template>
  <div>
    <div class="columns">
        <div class="column">
            <h4 class="title is-4">
                <span v-if="survey.is_quiz">Результаты тестирования</span>
                <span v-else>Участники анкеты</span>
            </h4>
            <h6 class="subtitle is-6">{{ survey.title }}</h6>
        </div>
        <div class="column is-one-third no-print">
            <b-field>
                <b-select v-model="filter.status" expanded>
                    <option :value="null">Все {{ survey.is_quiz ? 'бланки' : 'анкеты' }}</option>
                    <option value="finished">Только заполненные</option>
                    <option value="empty">Только незаполненные</option>
                    <option value="in_progress">В процессе заполнения</option>
                </b-select>
            </b-field>
            <b-field grouped>
                <div class="field is-expanded">
                    <button
                        @click="download"
                        class="button is-primary is-outlined is-fullwidth">
                        <b-icon icon="download" size="is-small"></b-icon>
                        <span>Скачать</span>
                    </button>
                </div>
                <div class="field is-expanded">
                    <button
                        @click="requestScoreReport"
                        :disabled="filter.status == 'empty' || filter.status == 'in_progress'"
                        class="button is-primary is-outlined is-fullwidth">
                        <b-icon icon="email-newsletter" size="is-small"></b-icon>
                        <span>Получить отчет</span>
                    </button>
                </div>
            </b-field>
        </div>
    </div>

    <template v-if="filteredParticipants.length > 0">
        <p class="has-text-centered">
            <span>{{ current }} из {{ total }}</span>
            <span>({{ percentage }}%)</span>
        </p>
        <progress class="progress is-primary is-small"
            :value="current"
            :max="total"
        />
        <small class="is-block has-text-centered has-text-grey no-print">
            Таблица обновляется автоматически
        </small>
    </template>

    <b-table
        v-if="initialLoadComplete"
        :data="filteredParticipants"
        default-sort="user.full_name"
        :paginated="false"
        narrowed
        striped>
        <template slot-scope="props">

            <b-table-column
                sortable
                field="user.full_name"
                label="Фамилия Имя Отчество">
                <a :href="'/' + $_.get(props.row, 'user.alias')" target="_blank">
                    <b-tooltip
                        multilined
                        v-if="$_.get(props.row, 'residual', false)"
                        position="is-right"
                        :label="
                            'Пользователь отвечал ранее, но сейчас не является участником ' +
                            (survey.is_quiz ? 'данного теста' : 'данной анкеты')
                        "
                    >
                        <b-icon
                        icon="plus-circle-outline"
                        size="is-small"
                        ></b-icon>
                    </b-tooltip>
                    <span>{{ $_.get(props.row, 'user.full_name') }}</span>
                    <i class="mdi mdi-open-in-new no-print"></i>
                </a>
            </b-table-column>

            <b-table-column
                centered
                field="participated"
                :visible="filter.status == null"
                :label="survey.is_quiz ? 'Бланк заполнен' : 'Анкета заполнена'">
                <b-tooltip :label="props.row.unverified ?
                    'Пользователь не зарегистрирован' :
                    props.row.in_progress ?
                    'В процессе заполнения' :
                    props.row.participated ?
                    'Ответ отправлен' :
                    ''"
                    :type="props.row.participated ?
                    'is-success' :
                    props.row.in_progress ?
                    'is-info' :
                    props.row.unverified ?
                    'is-danger' :
                    ''">
                    <b-icon :icon="props.row.participated ?
                    'check-circle-outline' :
                    props.row.in_progress ?
                    'timelapse' :
                    props.row.unverified ?
                    'alert-circle-outline' :
                    'checkbox-blank-circle-outline'"
                    :type="props.row.participated ?
                    'is-success' :
                    props.row.in_progress ?
                    'is-info' :
                    props.row.unverified ?
                    'is-danger' :
                    ''"
                    size="is-small"></b-icon>
                </b-tooltip>
            </b-table-column>
            
            <b-table-column
                sortable
                centered
                :visible="survey.is_quiz && (filter.status == null || filter.status == 'finished')"
                field="score"
                label="Результат">
                <template v-if="!props.row.participated">
                    <span>-</span>
                </template>
                <template v-else-if="props.row.score > -1">
                    <a class="button is-small is-outlined is-primary"
                        :href="getFormUrl(props.row)">
                        <span>{{ getScoreString(props.row) }}</span>
                    </a>
                    <b-tooltip label="Сбросить результаты" type="is-danger">
                        <button
                            @click="resetAnswers(props.row)"
                            class="button is-small is-outlined is-danger no-print">
                            <span class="icon mdi mdi-close mdi-12px"></span>
                        </button>
                    </b-tooltip>
                </template>
                <template v-else>
                    <a class="button is-small is-success"
                        :href="getFormUrl(props.row)">
                        <b-icon icon="star" size="is-small"></b-icon>
                        <span>Оценить</span>
                    </a>
                </template>
            </b-table-column>

        </template>
        
        <div slot="empty" class="has-text-centered">
            Список участников отобразится в течение нескольких минут
        </div>
    </b-table>

    <table-skeleton v-else></table-skeleton>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import toasts from "../../mixins/toasts";

let updateInterval = 15000;

export default Vue.extend({
  props: {
    emailHint: {
      type: String
    },
    survey: {
      type: Object,
      required: true
    }
  },
  mixins: [toasts],
  data() {
    return {
      filter: {
          status: null,
      },
      initialLoadComplete: false,
      participants: []
    };
  },
  created() {
    setInterval(() => {
      this.loadParticipants();
    }, updateInterval);

    this.loadParticipants().catch(error => {
      this.toast("error", "@unexpected", error);
    });
  },
  methods: {
    download() {
        let maxNameWidth = 1;

        let data = this.filteredParticipants.map((p) => {
            let entry = {
                "ФИО": p.user.full_name,
            };

            maxNameWidth =
                p.user.full_name.length > maxNameWidth
                    ? p.user.full_name.length
                    : maxNameWidth;

            if (this.survey.is_quiz && p.participated) {
                entry["Баллы"] = p.score;
                entry["%"] = parseInt(
                    (p.score / p.total_score) * 100
                );
            }

            return entry;
        });

        data = _.orderBy(data, ["ФИО"]);

        let wb = XLSX.utils.book_new();
        let ws = XLSX.utils.json_to_sheet(data);
        ws["!cols"] = [{ wch: maxNameWidth }];

        XLSX.utils.book_append_sheet(wb, ws, "Участники");
        
        let type =
            this.filter.status == "finished"
                ? "заполненные"
                : this.filter.status == "empty"
                ? "пустые"
                : this.filter.status == "in_progress"
                ? "в процессе заполнения"
                : `все ${this.survey.is_quiz ? "бланки" : "участники"}`;

        let title = `${this.survey.id} - ${type}.xlsx`;

        XLSX.writeFile(wb, title);
    },
    async requestScoreReport() {
      this.$dialog.confirm({
          title: "Отчет о результатах теста",
          message:
              `Вы собираетесь отправить запрос на создание отчета о результатах теста.<br>` +
              `Поскольку данная операция может занять некоторое время, отчет будет ` +
              `отправлен Вам в виде письма с прикреплением на E-Mail адрес, указанный в вашем профиле: ` +
              `<b>${this.emailHint}</b>`,
          confirmText: "Продолжить",
          cancelText: "Отмена",
          type: "is-info",
          hasIcon: true,
          onConfirm: () => {
              return axios
                  .post(
                      `/api/surveys/${this.survey.id}/report`
                  )
                  .then((response) => {
                      this.toast("info", "Запрос на создание отчета принят");
                  })
                  .catch((error) => {
                      this.toast("error", "@unexpected", error);
                  });
          },
      });
    },
    getScoreString(row) {
      let percentage = parseInt((row.score / row.total_score) * 100);
      return `${row.score} / ${row.total_score} (${percentage}%)`;
    },
    getFormUrl(row) {
      return `./score?user=${row.user.alias}`;
    },
    resetAnswers(row) {
        this.$dialog.confirm({
            title: "Сброс результатов",
            message:
                `Вы собираетесь сбросить результаты для студента ` +
                `<b>${row.user.full_name}</b>.<br>` +
                `Это удалит все ответы и статус о прохождении, что позволит ` +
                `пройти ${this.survey.is_quiz ? 'тест' : 'анкету'} заново.`,
            confirmText: "Сбросить",
            cancelText: "Отмена",
            type: "is-danger",
            hasIcon: true,
            onConfirm: () => {
                return axios
                    .delete(
                        `/api/surveys/${this.survey.id}/answers/${row.user.alias}`
                    )
                    .then((response) => {
                        this.loadParticipants();
                    })
                    .catch((error) => {
                        this.toast("error", "@unexpected", error);
                    });
            },
        });
    },
    loadParticipants() {
      return axios
        .get(`/api/surveys/${this.survey.id}/participants`)
        .then(response => {
          this.participants = response.data;
          this.initialLoadComplete = true;
        })
        .catch(error => {
          this.toast("error", "@unexpected", error);
        });
    }
  },
  computed: {
    filteredParticipants() {
        switch (this.filter.status) {
            case "in_progress": {
                return this.participants.filter(
                    (p) => p.in_progress == true
                );
            }
            case "finished": {
                return this.participants.filter(
                    (p) => p.participated == true
                );
            }
            case "empty": {
                return this.participants.filter(
                    (p) =>
                        p.participated == false &&
                        p.in_progress == false &&
                        p.unverified == false
                );
            }
        }

        return this.participants;
    },
    current() {
      return _.filter(this.participants, {
        participated: true
      }).length;
    },
    total() {
      return this.participants.length;
    },
    percentage() {
      return Math.ceil((this.current / this.total) * 100);
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/views/surveys/Participants.vue